import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Eye, EyeOff, LogIn, Building2, Lock, Mail,
  AlertCircle, ArrowLeft, FlaskConical, Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authAPI, publicAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LandingNavbar from '../../components/LandingNavbar';

const ContractorLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // Force password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenantBranding, setTenantBranding] = useState(null);

  // Debounced lookup of parent-lab branding based on the email the contractor typed.
  // This reveals the logo of the lab that created this contractor account —
  // e.g. if "مختبرات البرج" was created by lab "خالد", the Burj user sees Khalid's logo.
  // Falls back to the platform logo if no match (or no logo uploaded).
  useEffect(() => {
    if (!email || !email.includes('@')) {
      setTenantBranding(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const { data } = await publicAPI.getContractorBranding(email.trim().toLowerCase());
        if (data?.success && data?.data?.branding) {
          setTenantBranding(data.data);
        } else {
          setTenantBranding(null);
        }
      } catch (_) {
        setTenantBranding(null);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [email]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('يرجى إدخال البريد الإلكتروني وكلمة المرور');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      const { token, user } = response.data?.data || response.data;

      // Debug: log the user object to see the structure
      console.log('Login response user:', user);
      console.log('User role:', user.role);

      // Check if user is contractor_lab role
      // role is returned as string directly, not as object
      const userRole = typeof user.role === 'object' ? user.role?.name : user.role;
      console.log('Extracted userRole:', userRole);

      if (userRole !== 'contractor_lab') {
        setError('هذه البوابة مخصصة للمختبرات المتعاقدة فقط. الدور الحالي: ' + userRole);
        setLoading(false);
        return;
      }

      // Check if must change password first
      const mustChange = response.data?.data?.must_change_password;
      if (mustChange) {
        setTempToken(token);
        setTempUser(user);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }

      // Store contractor token and data
      localStorage.setItem('contractorToken', token);
      localStorage.setItem('contractorUser', JSON.stringify(user));

      toast.success('تم تسجيل الدخول بنجاح');
      navigate('/contractor/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'فشل تسجيل الدخول');
    }
    setLoading(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!newPassword || !confirmNewPassword) { setError('يرجى تعبئة جميع الحقول'); return; }
    if (newPassword.length < 8) { setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل'); return; }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) { setError('يجب أن تحتوي على حرف كبير وصغير ورقم'); return; }
    if (newPassword !== confirmNewPassword) { setError('كلمة المرور غير متطابقة'); return; }

    setLoading(true);
    try {
      // Use temp token for the change-password call.
      // REACT_APP_API_URL already contains `/api`, so the path is just `/auth/change-password`.
      const axios = (await import('axios')).default;
      const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
      await axios.post(`${apiBase}/auth/change-password`, {
        currentPassword: password,
        newPassword,
      }, { headers: { Authorization: `Bearer ${tempToken}` } });

      // Now login is complete
      localStorage.setItem('contractorToken', tempToken);
      localStorage.setItem('contractorUser', JSON.stringify(tempUser));
      toast.success('تم تغيير كلمة المرور بنجاح');
      navigate('/contractor/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'فشل تغيير كلمة المرور');
    }
    setLoading(false);
  };

  const features = [
    { label: 'استلام طلبات التحاليل', icon: FlaskConical },
    { label: 'تتبع حالة الطلبات مباشرة', icon: Check },
    { label: 'أسعار تعاقدية خاصة', icon: Building2 },
  ];

  // Force password change screen
  if (mustChangePassword) {
    return (
      <>
        <LandingNavbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 pt-[120px]" dir="rtl" style={{ fontFamily: "'Readex Pro', sans-serif" }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Lock className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">تغيير كلمة المرور</h2>
            <p className="text-sm text-gray-500 mt-1">يجب تغيير كلمة المرور المؤقتة قبل الدخول</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              <span className="text-sm text-red-600">{error}</span>
            </div>
          )}

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">كلمة المرور الجديدة</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" dir="ltr" placeholder="8 أحرف على الأقل — حرف كبير + صغير + رقم" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">تأكيد كلمة المرور</label>
              <input type="password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500" dir="ltr" />
            </div>
            <button type="submit" disabled={loading} className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {loading ? 'جاري التغيير...' : 'تغيير كلمة المرور والدخول'}
            </button>
          </form>
        </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <LandingNavbar />
      {/* Single focused card — the old split-screen green panel with
          floating shapes and feature icons was visual noise competing
          with the form. Contractor portal is a utility, not marketing. */}
      <div
        className="min-h-screen flex items-center justify-center px-6 pt-[120px] pb-16 relative"
        dir="rtl"
        style={{ fontFamily: "'Readex Pro', sans-serif", backgroundColor: '#FDFCF9' }}
      >
        {/* Subtle dotted texture. */}
        <div
          className="absolute inset-0 opacity-[0.4] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #0F6E56 0.8px, transparent 0)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.25), transparent 70%)',
          }}
        />

      <div className="w-full relative z-10 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-[440px] bg-white rounded-3xl border border-[#E8E5DD] shadow-[0_10px_40px_-10px_rgba(15,110,86,0.12)] p-8 md:p-10"
        >
          {/* Logo */}
          <div className="flex justify-center mb-6">
            {(tenantBranding?.branding?.logoUrl) ? (
              <img
                src={tenantBranding.branding.logoUrl}
                alt={tenantBranding.nameAr || tenantBranding.name || 'Lab'}
                className="h-10 w-auto object-contain"
              />
            ) : (
              <img src="/listech-logo.svg" alt="LIS Techs" className="h-9 w-auto" />
            )}
          </div>

          {/* Form Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: '#E1F5EE', color: '#0F6E56' }}
            >
              <Building2 className="w-4 h-4" />
              بوابة المختبرات المتعاقدة
            </div>
            <h2 className="text-3xl font-bold text-[#2C2C2A] mb-2 leading-[1.5]">تسجيل الدخول</h2>
            <p className="text-[#888780] leading-[1.7]">أهلا بك، يرجى إدخال بياناتك للمتابعة</p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2A] mb-2 leading-[1.7]">
                البريد الإلكتروني
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 pr-11 bg-white border border-gray-200 rounded-xl text-[#2C2C2A] placeholder-gray-400 focus:outline-none focus:border-[#1D9E75] focus:ring-2 focus:ring-[#1D9E75]/20 transition-all"
                  placeholder="email@hospital.com"
                  dir="ltr"
                />
                <Mail className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#888780]" />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-[#2C2C2A] mb-2 leading-[1.7]">
                كلمة المرور
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-11 pl-11 bg-white border border-gray-200 rounded-xl text-[#2C2C2A] placeholder-gray-400 focus:outline-none focus:border-[#1D9E75] focus:ring-2 focus:ring-[#1D9E75]/20 transition-all"
                  placeholder="••••••••"
                  dir="ltr"
                />
                <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#888780]" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#888780] hover:text-[#2C2C2A] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.99 }}
              className={`w-full py-3.5 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all ${
                loading
                  ? 'opacity-60 cursor-not-allowed'
                  : 'hover:shadow-xl'
              }`}
              style={{
                backgroundColor: '#0F6E56',
                boxShadow: loading ? 'none' : '0 8px 24px rgba(15, 110, 86, 0.3)',
              }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>جاري تسجيل الدخول...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>تسجيل الدخول</span>
                </>
              )}
            </motion.button>
          </form>

          {/* Help Text */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 p-4 rounded-xl border"
            style={{ backgroundColor: '#E1F5EE', borderColor: '#1D9E75' + '30' }}
          >
            <p className="text-sm font-medium mb-2" style={{ color: '#0F6E56' }}>كيف أحصل على حساب؟</p>
            <p className="text-sm" style={{ color: '#1D9E75' }}>
              يتم إنشاء حسابك من قبل إدارة المختبر بعد توقيع العقد. تواصل معنا للمزيد من المعلومات.
            </p>
          </motion.div>

          {/* Other Login Options */}
          <div className="mt-6 space-y-3 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-[#888780] hover:text-[#2C2C2A] flex items-center justify-center gap-2 mx-auto transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              هل أنت عميل؟ بوابة العملاء
            </button>
            <button
              onClick={() => navigate('/staff-login')}
              className="text-sm flex items-center justify-center gap-2 mx-auto transition-colors"
              style={{ color: '#0F6E56' }}
            >
              <ArrowLeft className="w-4 h-4" />
              هل أنت موظف في المختبر؟ بوابة الموظفين
            </button>
          </div>

          {/* Footer */}
          <p className="mt-8 text-center text-sm text-[#888780]/60 leading-[1.8]">
            © {new Date().getFullYear()} {tenantBranding?.name || 'LIS Techs'}. جميع الحقوق محفوظة
          </p>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default ContractorLogin;
